import cx from 'classnames';
import React from 'react';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import { WrapTextNodes } from '../../utils/WrapTextNodes';
import Spinner from '../Spinner';
import styles from './Button.module.scss';
import type { ButtonProps } from './Button.types';
import { AlignContentMap } from './Button.contants';

/**
 * Button component definition.
 */
const Button = (props: ButtonProps) => {
  const {
    className,
    appearance = 'Default',
    size = 'Medium',
    type = 'Filled',
    style = 'Primary',
    leadingIcon,
    trailingIcon,
    loading = false,
    disabled = false,
    onClick,
    children,
    actionType = 'button',
    alignContent = 'Center',
    ...restProps
  } = props;

  let leadingIconComponent, trailingIconComponent, content, loadingComponent;

  // If the button is in loading state replace content with a loading indicator
  if (loading) {
    loadingComponent = (
      <Spinner className={styles.loadingIcon} />
    );
  }

  // Render leading icon if it is passed
  if (leadingIcon) {
    leadingIconComponent = <div className={styles.leadingIcon}>
      {leadingIcon}
    </div>;
  }

  // Render training icon if it is passed
  if (trailingIcon) {
    trailingIconComponent = <div className={styles.trailingIcon}>
      {trailingIcon}
    </div>;
  }

  if (children) {
    content = <div className={styles.content}>
      <WrapTextNodes noWrap={true} className={styles.text}>
        {children}
      </WrapTextNodes>
    </div>;
  }

  const variantProps = useBuildVariantProps(
    'button',
    { appearance, size, style, type },
    { loading },
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading) {
      onClick?.(event);
    }
  };

  return (
    <button
      className={cx(
        styles.button,
        className,
        styles[AlignContentMap[alignContent]],
        {
          [styles.loading]: loading,
        },
      )}
      type={actionType}
      onClick={handleClick}
      disabled={disabled}
      {...variantProps}
      {...restProps}
    >
      {loadingComponent}
      {leadingIconComponent}
      {content}
      {trailingIconComponent}
    </button>
  );
};

export default Button;
